import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

export type StatusKind =
  | 'compliant'
  | 'non_compliant'
  | 'testing'
  | 'to_sample'
  | 'active'
  | 'archived'
  | 'pending'
  | 'suspended'
  | 'removed'
  | 'left'
  | 'expired'
  | 'deleted'
  | 'read_only';

/** Etichette e colori degli stati della guida di design: lo stato porta sempre la parola. */
export const STATUS_STYLES: Record<StatusKind, { label: string; classes: string }> = {
  compliant: {
    label: 'Conforme',
    classes: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
  },
  non_compliant: {
    label: 'Non conforme',
    classes: 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300',
  },
  testing: {
    label: 'In prova',
    classes: 'bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-300',
  },
  to_sample: {
    label: 'Da prelevare',
    classes: 'bg-bsite-100 text-bsite-700 dark:bg-bsite-950 dark:text-bsite-200',
  },
  active: {
    label: 'Attivo',
    classes: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
  },
  archived: {
    label: 'Archiviato',
    classes: 'bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-300',
  },
  pending: {
    label: 'In attesa',
    classes: 'bg-bsite-100 text-bsite-700 dark:bg-bsite-950 dark:text-bsite-200',
  },
  suspended: {
    label: 'Sospeso',
    classes: 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300',
  },
  removed: {
    label: 'Rimosso',
    classes: 'bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-300',
  },
  left: {
    label: 'Uscito',
    classes: 'bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-300',
  },
  expired: {
    label: 'Scaduto',
    classes: 'bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-300',
  },
  deleted: {
    label: 'Eliminato',
    classes: 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300',
  },
  read_only: {
    label: 'Sola lettura',
    classes: 'bg-bsite-100 text-bsite-700 dark:bg-bsite-950 dark:text-bsite-200',
  },
};

/** Etichetta di stato a pillola con pallino. */
@Component({
  selector: 'bs-status-badge',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'inline-flex' },
  template: `
    <span [class]="classes()" [attr.data-status]="status()">
      <span class="size-1.5 rounded-full bg-current" aria-hidden="true"></span>
      {{ text() }}
    </span>
  `,
})
export class StatusBadgeComponent {
  readonly status = input.required<StatusKind>();
  /** Testo alternativo all'etichetta standard dello stato. */
  readonly label = input<string | null>(null);

  protected readonly text = computed(() => this.label() ?? STATUS_STYLES[this.status()].label);
  protected readonly classes = computed(
    () =>
      `inline-flex h-6 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 text-xs font-semibold ring-1 ring-inset ring-current/20 ${STATUS_STYLES[this.status()].classes}`,
  );
}
